import { PERMISSION_SCHEMA } from 'config';
import { SSO_PATHS } from 'config';
import jwtDecode from 'jwt-decode';
import diff from 'diff-arrays-of-objects';
import { format } from 'date-fns';
import _ from 'lodash';

export const objectToQueryString = (obj = {}) => {
  const queryParams = [];

  for (let key in obj) {
    const encodedKey = encodeURIComponent(key);
    const encodedValue = encodeURIComponent(obj[key]);
    queryParams.push(`${encodedKey}=${encodedValue}`);
  }

  return queryParams.join('&');
};

export const convertPrice = (number, { skipCurrency = false } = {}) => {
  if (number == '' || number == null) {
    return number;
  }
  if (skipCurrency) {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(number);
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(number);
};

export const launchPDF = (link) => {
  window.open(link, '_new', 'popup,left=100,top=100,width=700,height=900');
};

export const launchPDFS3 = async (link, payorID) => {
  const data = {
    path: link,
    payorId: payorID
  };
  const s3LinkResponse = await getSignedURL(data);
  const s3Link = s3LinkResponse.signedUrl;
  window.open(s3Link, '_new', 'popup,left=100,top=100,width=700,height=900');
};

export const getSSOServerBasePath = () => {
  return SSO_PATHS[import.meta.env.VITE_ENVIRONMENT] || SSO_PATHS.QA;
};

export const getSSOAuthUrl = () => {
  // Get the current protocol (http: or https:)
  const protocol = window.location.protocol;
  // Get the current host (hostname + port, if specified)
  const host = window.location.host;
  return `${getSSOServerBasePath()}/auth?redirectUri=${protocol}//${host}`;
};

export const getSSORefreshTokenUrl = () => {
  return `${getSSOServerBasePath()}/refreshToken`;
};

export const getSSOLogoutUrl = () => {
  return `${getSSOServerBasePath()}/logout`;
};

export const getSSOInvalidateTokenUrl = () => {
  return `${getSSOServerBasePath()}/invalidateToken`;
};

export const getSSOSignInUrl = () => {
  return `${getSSOServerBasePath()}/auth/signin`;
};

export const getParamValueByKey = (search, key) => {
  const qParams = new URLSearchParams(search);
  return qParams.get(key);
};

export const getParsedJwtValue = () => {
  const serviceToken = window.localStorage.getItem('serviceToken');
  if (!serviceToken) {
    return null;
  }
  const decoded = jwtDecode(serviceToken);
  return decoded;
};

export const hasDifference = (original, updated) => {
  var result = diff(original, updated);
  if (result.added?.length || result.updated?.length || result.removed?.length) {
    return true;
  }
  return false;
};
// export const mapPermissionToSchema = (permissions) => {
//   let mappedPermissions = PERMISSION_SCHEMA;
//   permissions?.forEach((permission) => {
//     /// Compatibility Patch
//     // if (!Object.keys(PERMISSION_SCHEMA).includes(permission))
//     //   return
//     ///
//     mappedPermissions[permission.module][permission.perm_key] = true;
//   });
//   return mappedPermissions;
// };
export const mapPermissionToSchema = (permissions) => {
  const mappedPermissions = {};

  permissions?.forEach((permission) => {
    const { module, perm_key } = permission;
    // Initialize module if not already present
    if (!mappedPermissions[module]) {
      mappedPermissions[module] = {};
    }
    // Set permission to true
    mappedPermissions[module][perm_key] = true;
  });
  // Fill in missing permissions from PERMISSION_SCHEMA with false
  Object.keys(mappedPermissions).forEach((module) => {
    const defaultPerms = PERMISSION_SCHEMA[module];
    if (defaultPerms) {
      Object.keys(defaultPerms).forEach((permKey) => {
        if (mappedPermissions[module][permKey] === undefined) {
          mappedPermissions[module][permKey] = false;
        }
      });
    }
  });

  return mappedPermissions;
};

export const getModulePermission = (schema, moduleName) => {
  let hasPermission = false;
  const moduleChildren = schema[moduleName];
  if (moduleChildren) {
    Object.keys(moduleChildren).forEach((module) => {
      if (moduleChildren[module] === true) {
        hasPermission = moduleChildren[module];
      }
    });
  }
  return hasPermission;
};

export const getParentModule = (childPermission) => {
  for (const module in PERMISSION_SCHEMA) {
    if (Object.keys(PERMISSION_SCHEMA[module]).includes(childPermission)) {
      return module;
    }
  }
  return null; // Child permission not found in any module
};

export const getFormattedDate = (dateString) => {
  if (!dateString) return '-';
  const dt = new Date(dateString);
  const dtDateOnly = new Date(dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000);
  const formattedDate = format(dtDateOnly, 'MM/dd/yyyy');
  return formattedDate === 'Invalid Date' ? '-' : formattedDate;
};

export const transformData = (data) => {
  const typeWiseData = {};
  data.forEach((element) => {
    const type = element['typeName'];
    if (['fee schedule', 'derived', 'negotiated', 'Fee Schedule'].includes(type)) {
      if (!typeWiseData['negotiated']) {
        typeWiseData['negotiated'] = [];
      }
      typeWiseData['negotiated'].push(element);
    } else {
      if (!typeWiseData[type]) {
        typeWiseData[type] = [];
      }
      typeWiseData[type].push(element);
    }
  });
  return typeWiseData;
};

const transformDataFilteredDrg = (arr = []) => {
  let columns = [];
  const rowsObj = arr.reduce((acc, elem) => {
    columns.push(elem.reporting_entity_name);
    if (!acc[elem.billing_code])
      acc[elem.billing_code] = {
        billing_code: elem.billing_code,
        hospital: elem.hospital,
        negotiated_type: elem.negotiated_type,
        description: elem.description,
        reporting_entity_list: { rate: [] }
      };
    acc[elem.billing_code].reporting_entity_list[elem.reporting_entity_name]
      ? acc[elem.billing_code].reporting_entity_list[elem.reporting_entity_name].rate.push(elem.rate)
      : (acc[elem.billing_code].reporting_entity_list[elem.reporting_entity_name] = { rate: [elem.rate] });
    return acc;
  }, {});
  columns = [...new Set(columns)];
  const rows = [];
  for (const [key, value] of Object.entries(rowsObj)) {
    rows.push({
      billing_code: key,
      hospital: value.hospital,
      negotiated_type: value.negotiated_type,
      description: value.description,
      ...columns.reduce((acc, elem) => {
        acc[elem] = value.reporting_entity_list[elem] ? _.mean(value.reporting_entity_list[elem].rate) : 0;
        return acc;
      }, {})
    });
  }
  return { columns, rows };
};

export const getAvg = (data, key) => {
  let filterData = data?.filter((x) => {
    return x[key] !== '' && typeof x[key] == 'number' && x[key];
  });
  return filterData.reduce((acc, x) => acc + x[key], 0) / filterData?.length;
};

export const getMin = (data, key) => {
  return data?.filter((x) => x[key] == Math.min(...data.map((x) => x[key])));
};

export const getMax = (data, key) => {
  return data?.filter((x) => x[key] == Math.max(...data.map((x) => x[key])));
};

export const sortJsonArr = (data, key, type) => {
  let result = [];
  if (type == 'asc') {
    result = data?.sort((a, b) => parseFloat(a[key]) - parseFloat(b[key]));
  } else if (type == 'dsc') {
    result = data?.sort((a, b) => parseFloat(b[key]) - parseFloat(a[key]));
  } else {
    result = data;
  }
  return result;
};

export const setPriceCategoryList = (data) => {
  let dataList = data;
  let maxPrice = [];
  let minPrice = [];
  let average = 0;
  maxPrice = getMax(dataList, 'price');
  minPrice = getMin(dataList, 'price');
  average = getAvg(dataList, 'price');
  let result = [];
  result.push(
    {
      id: 3,
      priceCat: 'Minimum Price',
      value: minPrice && minPrice[0]?.price
    },
    {
      id: 1,
      priceCat: 'Maximum Price',
      value: maxPrice && maxPrice[0]?.price
    },
    {
      id: 2,
      priceCat: 'Average Price',
      value: average && average?.toFixed(0)
    }
  );
  return result;
};

export const setPriceDiffAvgChartList = (data) => {
  let dataList = data;
  let average = getAvg(dataList, 'price').toFixed(2);

  let result = [
    {
      id: 'chart',
      data: []
    }
  ];
  let resultDataArr = dataList?.map((item) => {
    return {
      x: item.payerName,
      y: (average - item.price)?.toFixed() * -1,
      priceDiff: (average - item.price)?.toFixed(2),
      priceDiffPercentage: (((average - item.price) * 100) / parseFloat(average))?.toFixed()
    };
  });
  result[0].data = sortJsonArr(resultDataArr, 'y', 'asc');
  return result;
};

export const addString = (value, type, str) => {
  let result;
  if (typeof value == 'number') {
    result = type == 'pre' ? str + '' + value : value + '' + str;
  } else {
    result = value;
  }
  return result;
};

export const compareObjects = (obj1, obj2) => {
  try {
    const one = typeof obj1 !== 'string' ? JSON.stringify(obj1) : obj1;
    const two = typeof obj2 !== 'string' ? JSON.stringify(obj2) : obj2;
    return one == two;
  } catch (e) {
    return false;
  }
};

export const splitHospitalWiseData = (data = []) => {
  const hospitalWiseData = data.reduce((acc, elem) => {
    if (!acc[elem.hospital]) acc[elem.hospital] = [];
    acc[elem.hospital].push(elem);
    return acc;
  }, {});

  let c = [];
  let r = [];
  for (const [key, value] of Object.entries(hospitalWiseData)) {
    const { columns, rows } = transformDataFilteredDrg(value);
    c = c.concat(columns);
    r = r.concat(rows);
  }
  return { columns: [...new Set(c)], rows: r };
};
